import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

/* ─────────────────────────────────────────
   STORAGE KEY — set once, never shown again
───────────────────────────────────────── */
const INTRO_SEEN_KEY = 'homegame_intro_seen';

const hasSeenIntro = () => {
  try { return localStorage.getItem(INTRO_SEEN_KEY) === 'true'; } catch { return false; }
};
const markIntroSeen = () => {
  try { localStorage.setItem(INTRO_SEEN_KEY, 'true'); } catch {}
};

/* ─────────────────────────────────────────
   SLIDE DATA
───────────────────────────────────────── */
const BAYBAYIN_CHARS = [
  { glyph: 'ᜀ', latin: 'A'   },
  { glyph: 'ᜊ', latin: 'BA'  },
  { glyph: 'ᜃ', latin: 'KA'  },
  { glyph: 'ᜇ', latin: 'DA'  },
  { glyph: 'ᜄ', latin: 'GA'  },
  { glyph: 'ᜑ', latin: 'HA'  },
  { glyph: 'ᜁ', latin: 'I'   },
  { glyph: 'ᜎ', latin: 'LA'  },
  { glyph: 'ᜋ', latin: 'MA'  },
  { glyph: 'ᜈ', latin: 'NA'  },
  { glyph: 'ᜅ', latin: 'NGA' },
  { glyph: 'ᜂ', latin: 'O/U' },
  { glyph: 'ᜉ', latin: 'PA'  },
  { glyph: 'ᜍ', latin: 'RA'  },
  { glyph: 'ᜐ', latin: 'SA'  },
  { glyph: 'ᜆ', latin: 'TA'  },
  { glyph: 'ᜏ', latin: 'WA'  },
  { glyph: 'ᜌ', latin: 'YA'  },
];

const SLIDES = [
  {
    id: 'welcome',
    eyebrow: 'Welcome',
    title: 'Discover Baybayin',
    body: 'Baybayin is an ancient Filipino script used before Spanish colonisation. Each character represents a syllable — a unique writing system that connects you to Philippine history and culture.',
    accent: '#fbc417',
    icon: '📜',
    extra: 'script',
  },
  {
    id: 'characters',
    eyebrow: 'The Alphabet',
    title: '18 Core Characters',
    body: 'Baybayin has 17 consonant-vowel syllables plus standalone vowels. Every consonant automatically carries an "A" sound — diacritical marks modify the vowel.',
    accent: '#f97316',
    icon: 'ᜊ',
    extra: 'charGrid',
  },
  {
    id: 'write',
    eyebrow: 'Game Mode 1',
    title: 'Write Mode ✍️',
    body: 'A Latin character appears on screen. Draw its Baybayin equivalent on the canvas using mouse or touch. Our AI checks your stroke in real time. You have 60 seconds — score as many as you can!',
    accent: '#22c55e',
    icon: '✍️',
    extra: 'writeMech',
  },
  {
    id: 'translate',
    eyebrow: 'Game Mode 2',
    title: 'Translate Mode 🔄',
    body: 'Test your recognition with three sub-modes: Multiple Choice (pick the correct answer), Typing (type the Latin equivalent), and Drag & Drop (match tiles to drop zones). Pick your difficulty!',
    accent: '#38bdf8',
    icon: '🔄',
    extra: 'translateMech',
  },
  {
    id: 'tap',
    eyebrow: 'Game Mode 3',
    title: 'Tap Mode 👆',
    body: 'An image appears — an animal, fruit, color, or object. Tap the correct Baybayin tiles to spell its name. You have 30 seconds per question. Choose from 5 themed categories!',
    accent: '#a78bfa',
    icon: '👆',
    extra: 'tapMech',
  },
  {
    id: 'ready',
    eyebrow: "You're all set!",
    title: 'Magsimula na! 🎉',
    body: "That's everything you need to know. Pick a mode, choose your difficulty, and start practising Baybayin. Good luck — and have fun!",
    accent: '#fbc417',
    icon: '🏆',
    extra: 'ready',
  },
];

/* ─────────────────────────────────────────
   ANIMATIONS
───────────────────────────────────────── */
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const fadeUp = keyframes`from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}`;
const scaleIn = keyframes`from{opacity:0;transform:scale(0.88)}to{opacity:1;transform:scale(1)}`;
const shimmer = keyframes`0%,100%{opacity:.55}50%{opacity:1}`;
const floatY = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}`;
const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const pulse = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(251,196,23,.45)}50%{box-shadow:0 0 0 12px rgba(251,196,23,0)}`;

/* ─────────────────────────────────────────
   STYLED COMPONENTS
───────────────────────────────────────── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(10,5,2,0.92);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.35s ease;
  padding: 16px;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  background: linear-gradient(145deg, #1e0d04 0%, #2c1004 50%, #1a0802 100%);
  border: 1px solid rgba(251,196,23,0.25);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${scaleIn} 0.4s cubic-bezier(0.34,1.56,0.64,1);
  box-shadow:
    0 0 0 1px rgba(251,196,23,0.08),
    0 32px 80px rgba(0,0,0,0.7),
    inset 0 1px 0 rgba(251,196,23,0.12);
`;

const TopBar = styled.div`
  height: 4px;
  background: linear-gradient(90deg, transparent, ${p=>p.$accent||'#fbc417'}, transparent);
  opacity: 0.8;
`;

const CardBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px 20px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  @media(max-width:480px){ padding: 20px 20px 16px; }
`;

const Eyebrow = styled.p`
  font-family: 'Georgia', serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: ${p => p.$accent || '#fbc417'};
  margin: 0 0 8px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.05s;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.1s;
`;

const BigIcon = styled.span`
  font-size: 2.2rem;
  line-height: 1;
  animation: ${floatY} 3s ease-in-out infinite;
  display: inline-block;
`;

const Title = styled.h2`
  font-family: 'Georgia', serif;
  font-size: clamp(1.4rem, 4vw, 1.9rem);
  font-weight: 900;
  color: #fde68a;
  margin: 0;
  line-height: 1.15;
  text-shadow: 0 2px 12px rgba(251,196,23,0.25);
`;

const Body = styled.p`
  font-size: 0.92rem;
  line-height: 1.7;
  color: rgba(253,230,138,0.75);
  margin: 0 0 20px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.15s;
`;

/* ── Character grid (slide 2) ── */
const CharGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-bottom: 20px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.2s;
  @media(max-width:400px){ grid-template-columns: repeat(5, 1fr); }
`;

const CharCell = styled.div`
  background: rgba(251,196,23,0.07);
  border: 1px solid rgba(251,196,23,0.15);
  border-radius: 10px;
  padding: 6px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  transition: background 0.2s, transform 0.2s;
  &:hover {
    background: rgba(251,196,23,0.16);
    transform: translateY(-2px);
  }
`;

const CharGlyph = styled.span`
  font-size: 1.4rem;
  color: #fde68a;
  line-height: 1;
`;

const CharLatin = styled.span`
  font-size: 0.6rem;
  color: rgba(253,230,138,0.55);
  font-weight: 700;
  letter-spacing: 0.5px;
`;

/* ── Mechanics cards ── */
const MechGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.2s;
`;

const MechCard = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-left: 3px solid ${p => p.$accent || '#fbc417'};
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const MechIcon = styled.span`
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 1px;
`;

const MechText = styled.div``;

const MechTitle = styled.p`
  font-family: 'Georgia', serif;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${p => p.$accent || '#fbc417'};
  margin: 0 0 2px;
  letter-spacing: 0.5px;
`;

const MechDesc = styled.p`
  font-size: 0.78rem;
  color: rgba(253,230,138,0.6);
  margin: 0;
  line-height: 1.5;
`;

/* ── Ready slide ── */
const ReadyRing = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid rgba(251,196,23,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 2.4rem;
  animation: ${pulse} 2s ease infinite;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 1px solid rgba(251,196,23,0.12);
    animation: ${spin} 8s linear infinite;
    border-top-color: rgba(251,196,23,0.4);
  }
`;

/* ── Progress bar ── */
const ProgressBar = styled.div`
  height: 3px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  margin-bottom: 20px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, ${p=>p.$accent||'#fbc417'}, rgba(255,255,255,0.6));
  transition: width 0.5s cubic-bezier(0.4,0,0.2,1);
`;

/* ── Dot navigation ── */
const DotRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 16px;
`;

const NavDot = styled.button`
  width: ${p => p.$active ? '22px' : '7px'};
  height: 7px;
  border-radius: 4px;
  border: none;
  background: ${p => p.$active
    ? (p.$accent || '#fbc417')
    : 'rgba(255,255,255,0.18)'};
  cursor: pointer;
  padding: 0;
  transition: width 0.3s, background 0.3s;
`;

/* ── Footer ── */
const Footer = styled.div`
  padding: 0 32px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  @media(max-width:480px){ padding: 0 20px 20px; }
`;

const SkipBtn = styled.button`
  background: none;
  border: none;
  color: rgba(253,230,138,0.35);
  font-size: 0.78rem;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: color 0.2s;
  font-family: 'Georgia', serif;
  &:hover { color: rgba(253,230,138,0.65); }
`;

const NextBtn = styled.button`
  position: relative;
  background: ${p => p.$accent || '#fbc417'};
  border: none;
  color: #1a0802;
  font-family: 'Georgia', serif;
  font-size: 0.9rem;
  font-weight: 900;
  letter-spacing: 0.5px;
  padding: 11px 28px;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 20px rgba(251,196,23,0.3);
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(251,196,23,0.45);
  }
  &:active { transform: translateY(0); }
`;

const BtnShimmer = styled.span`
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%);
  transform: translateX(-100%);
  animation: shimmerSlide 2.5s ease infinite;
  @keyframes shimmerSlide { 0%{transform:translateX(-100%)} 60%,100%{transform:translateX(200%)} }
`;

/* ── Decorative corner ornaments ── */
const CornerOrn = styled.div`
  position: absolute;
  width: 36px;
  height: 36px;
  opacity: 0.18;
  pointer-events: none;
  ${p => p.$pos === 'tl' && 'top:0;left:0;border-top:2px solid #fbc417;border-left:2px solid #fbc417;border-radius:0 0 8px 0;'}
  ${p => p.$pos === 'tr' && 'top:0;right:0;border-top:2px solid #fbc417;border-right:2px solid #fbc417;border-radius:0 0 0 8px;'}
  ${p => p.$pos === 'bl' && 'bottom:0;left:0;border-bottom:2px solid #fbc417;border-left:2px solid #fbc417;border-radius:0 8px 0 0;'}
  ${p => p.$pos === 'br' && 'bottom:0;right:0;border-bottom:2px solid #fbc417;border-right:2px solid #fbc417;border-radius:8px 0 0 0;'}
`;

/* ── Ancient script decorative header ── */
const AncientBand = styled.div`
  background: rgba(10,5,2,0.5);
  border-bottom: 1px solid rgba(251,196,23,0.1);
  padding: 6px 32px;
  text-align: center;
  font-family: 'Georgia', serif;
  font-size: 13px;
  color: rgba(251,196,23,0.3);
  letter-spacing: 10px;
  animation: ${shimmer} 4s ease infinite;
  @media(max-width:480px){ padding: 6px 16px; letter-spacing: 6px; }
`;

/* ─────────────────────────────────────────
   EXTRA CONTENT RENDERERS
───────────────────────────────────────── */
const renderExtra = (extra, accent) => {
  if (extra === 'charGrid') {
    return (
      <CharGrid>
        {BAYBAYIN_CHARS.map(c => (
          <CharCell key={c.latin}>
            <CharGlyph>{c.glyph}</CharGlyph>
            <CharLatin>{c.latin}</CharLatin>
          </CharCell>
        ))}
      </CharGrid>
    );
  }

  if (extra === 'writeMech') {
    return (
      <MechGrid>
        {[
          { icon:'🖊️', title:'Draw on Canvas', desc:'Use your mouse or finger to trace the Baybayin character on the drawing pad.' },
          { icon:'🤖', title:'AI Checks Your Stroke', desc:'Our model analyses your drawing and tells you instantly if it matches the target character.' },
          { icon:'⏱️', title:'60-Second Rush', desc:'Score as many correct answers as possible before the timer hits zero.' },
          { icon:'💡', title:'Hint After 40s', desc:'A reference panel with all level characters appears if you need a memory boost.' },
        ].map(m => (
          <MechCard key={m.title} $accent={accent}>
            <MechIcon>{m.icon}</MechIcon>
            <MechText>
              <MechTitle $accent={accent}>{m.title}</MechTitle>
              <MechDesc>{m.desc}</MechDesc>
            </MechText>
          </MechCard>
        ))}
      </MechGrid>
    );
  }

  if (extra === 'translateMech') {
    return (
      <MechGrid>
        {[
          { icon:'🧠', title:'Multiple Choice', desc:'A Baybayin character is shown — pick the correct Latin syllable from 4 options.' },
          { icon:'⌨️', title:'Typing Mode', desc:'See a Baybayin character and type its Latin equivalent. A hint reveals the first letter.' },
          { icon:'🧩', title:'Drag & Drop', desc:'Drag Baybayin tiles from the choices column into the correct drop zone. Watch for glow feedback!' },
          { icon:'🎯', title:'Three Difficulties', desc:'Easy, Medium, and Hard — each unlocks a wider range of characters to challenge you.' },
        ].map(m => (
          <MechCard key={m.title} $accent={accent}>
            <MechIcon>{m.icon}</MechIcon>
            <MechText>
              <MechTitle $accent={accent}>{m.title}</MechTitle>
              <MechDesc>{m.desc}</MechDesc>
            </MechText>
          </MechCard>
        ))}
      </MechGrid>
    );
  }

  if (extra === 'tapMech') {
    return (
      <MechGrid>
        {[
          { icon:'🖼️', title:'Image Prompt', desc:'A picture of an animal, fruit, color, thing, or profession appears on screen.' },
          { icon:'🔤', title:'Tap to Spell', desc:'Tap the correct Baybayin tiles in order to spell out the Filipino name of the image.' },
          { icon:'⏰', title:'30-Second Timer', desc:'Quick rounds keep the pace intense — skip questions you are unsure about.' },
          { icon:'🗂️', title:'5 Categories', desc:'Animal, Profession, Fruit, Things, and Color — each with its own vocabulary set.' },
        ].map(m => (
          <MechCard key={m.title} $accent={accent}>
            <MechIcon>{m.icon}</MechIcon>
            <MechText>
              <MechTitle $accent={accent}>{m.title}</MechTitle>
              <MechDesc>{m.desc}</MechDesc>
            </MechText>
          </MechCard>
        ))}
      </MechGrid>
    );
  }

  if (extra === 'ready') {
    return (
      <ReadyRing style={{ animation: 'none' }}>
        🏆
      </ReadyRing>
    );
  }

  if (extra === 'script') {
    return (
      <CharGrid style={{ marginBottom: 20 }}>
        {BAYBAYIN_CHARS.slice(0, 6).map(c => (
          <CharCell key={c.latin}>
            <CharGlyph>{c.glyph}</CharGlyph>
            <CharLatin>{c.latin}</CharLatin>
          </CharCell>
        ))}
      </CharGrid>
    );
  }

  return null;
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const GameIntroTutorial = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  /* Only show to new users */
  useEffect(() => {
    if (!hasSeenIntro()) {
      setVisible(true);
    }
  }, []);

  const slide = SLIDES[step];
  const progress = ((step + 1) / SLIDES.length) * 100;
  const isLast = step === SLIDES.length - 1;

  const handleNext = () => {
    if (isLast) {
      finish();
    } else {
      setStep(s => s + 1);
    }
  };

  const finish = () => {
    markIntroSeen();
    setVisible(false);
    if (onDone) onDone();
  };

  if (!visible) return null;

  return (
    <Overlay>
      <Card key={slide.id}>
        <CornerOrn $pos="tl" />
        <CornerOrn $pos="tr" />
        <CornerOrn $pos="bl" />
        <CornerOrn $pos="br" />

        <TopBar $accent={slide.accent} />
        <AncientBand>ᜊᜌᜊᜌᜈ · ᜊᜌᜊᜌᜈ · ᜊᜌᜊᜌᜈ</AncientBand>

        <CardBody key={`body-${step}`}>
          <Eyebrow $accent={slide.accent}>{slide.eyebrow}</Eyebrow>
          <TitleRow>
            <BigIcon>{slide.icon}</BigIcon>
            <Title>{slide.title}</Title>
          </TitleRow>
          <Body>{slide.body}</Body>
          {renderExtra(slide.extra, slide.accent)}
        </CardBody>

        <div style={{ padding: '0 32px' }}>
          <ProgressBar>
            <ProgressFill $accent={slide.accent} style={{ width: `${progress}%` }} />
          </ProgressBar>
          <DotRow>
            {SLIDES.map((s, i) => (
              <NavDot
                key={s.id}
                $active={i === step}
                $accent={slide.accent}
                onClick={() => setStep(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </DotRow>
        </div>

        <Footer>
          <SkipBtn onClick={finish}>
            Skip intro
          </SkipBtn>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {step > 0 && (
              <SkipBtn onClick={() => setStep(s => s - 1)} style={{ color: 'rgba(253,230,138,0.5)' }}>
                ← Back
              </SkipBtn>
            )}
            <NextBtn $accent={slide.accent} onClick={handleNext}>
              <BtnShimmer />
              {isLast ? 'Start Playing! →' : 'Next →'}
            </NextBtn>
          </div>
        </Footer>
      </Card>
    </Overlay>
  );
};

export default GameIntroTutorial;